import React, { useState } from 'react';
import { useFormik } from 'formik';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  Button,
  Typography,
} from '@mui/material';
import MDButton from 'components/MDButton';
import * as yup from 'yup';
import Swal from 'sweetalert2';
import api from 'core/interceptors/api';
import { ServerBaseUrlForStaticImages } from 'constants';

// ✅ Constants
const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB

// ✅ Validation Schema
const validationServiceTypeSchema = yup.object().shape({
  service_type: yup
    .string()
    .required('Service Type is required')
    .min(3, 'Service Type must be at least 3 characters.')
    .max(50, 'Service Type must not exceed 50 characters.')
    .matches(
      /^[A-Za-z ]+$/,
      'Service Type must only contain letters and spaces (no numbers or special characters).'
    ),
});

export default function ServiceTypeEditModel({ open, handleClose, editInput }) {
  const initialValues = {
    service_type: editInput?.service_type_name || '',
    service_type_id: editInput?.id || '',
  };

  const [selectedImage, setSelectedImage] = useState(null);
  const [imageError, setImageError] = useState('');
  const apiBase = ServerBaseUrlForStaticImages;

  const formik = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    validationSchema: validationServiceTypeSchema,
    onSubmit: async (values, { resetForm }) => {
      if (imageError) return;

      try {
        const formData = new FormData();
        formData.append('service_type', values.service_type);
        if (selectedImage) {
          formData.append('service_type_image', selectedImage);
        }

        const response = await api.put(
          `/service_management/service_types/update_with_image/${values.service_type_id}`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }
        );

        resetForm();
        setSelectedImage(null);
        setImageError('');
        handleClose();

        if (response.data.status === 1) {
          Swal.fire({
            icon: 'success',
            title: 'Service Type Updated Successfully!',
            timer: 3000,
            showConfirmButton: false,
          });
        } else if (response.data.status === 2) {
          Swal.fire({
            icon: 'error',
            title: 'Service Type Already Exists!',
            timer: 3000,
            showConfirmButton: false,
          });
        }
      } catch (error) {
        console.error('Error updating service type:', error);
        Swal.fire({
          icon: 'error',
          title: 'Failed to update service type',
          text: error?.response?.data?.error || 'Something went wrong',
        });
        handleClose();
      }
    },
  });

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > MAX_FILE_SIZE) {
        setImageError('Image must be less than or equal to 2MB.');
        setSelectedImage(null);
      } else {
        setSelectedImage(file);
        setImageError('');
      }
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>Edit Service Type</DialogTitle>
      <form onSubmit={formik.handleSubmit}>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Service Type"
                name="service_type"
                fullWidth
                margin="normal"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.service_type}
                error={
                  formik.touched.service_type &&
                  Boolean(formik.errors.service_type)
                }
                helperText={
                  formik.touched.service_type &&
                  formik.errors.service_type
                }
              />
            </Grid>

            <Grid item xs={12}>
              {/* ✅ Preview of existing image if no new image selected */}
              {editInput?.service_type_image && !selectedImage && (
                <img
                  src={`${apiBase}/service_management/service_types/service_types/image/${editInput.service_type_image}`}
                  alt="Current"
                  style={{
                    width: '100%',
                    maxHeight: '180px',
                    objectFit: 'contain',
                    borderRadius: '8px',
                    marginBottom: '10px',
                    border: '1px solid #ccc',
                  }}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'https://via.placeholder.com/150?text=No+Image';
                  }}
                />
              )}

              {/* ✅ Preview of new selected image */}
              {selectedImage && (
                <img
                  src={URL.createObjectURL(selectedImage)}
                  alt="New Preview"
                  style={{
                    width: '100%',
                    maxHeight: '180px',
                    objectFit: 'contain',
                    borderRadius: '8px',
                    marginBottom: '10px',
                    border: '1px solid #ccc',
                  }}
                />
              )}

              <Button
                variant="contained"
                component="label"
                fullWidth
                color="info"
              >
                {selectedImage
                  ? selectedImage.name
                  : 'Upload New Image (Optional, ≤ 2MB)'}
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleImageChange}
                />
              </Button>

              {imageError && (
                <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                  {imageError}
                </Typography>
              )}
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => {
              formik.resetForm();
              setSelectedImage(null);
              setImageError('');
              handleClose();
            }}
            color="primary"
          >
            Cancel
          </Button>
          <MDButton
            type="submit"
            color="success"
            disabled={Boolean(imageError)}
          >
            Save
          </MDButton>
        </DialogActions>
      </form>
    </Dialog>
  );
}
